/**
 * Single derivation of client-facing redemption / early-withdrawal copy from
 * funds' product terms (audit #138 — sale correctness).
 *
 * Every surface that talks about a product's liquidity renders THROUGH these
 * helpers, so the copy can never diverge from the configured terms again:
 *  · no surface may promise liquidity funds does not grant ("liquid on
 *    request", a green "None" fee) — unknown terms read as unavailable;
 *  · with no penalty configured, early withdrawal is REFUSED by funds
 *    (early-and-free is rejected by the business rule), so the copy says the
 *    capital is committed — never that exiting is free.
 */

export interface RedemptionTermsInput {
  readonly terms_available: boolean;
  readonly term_days: number | null;
  readonly lockup_days?: number | null;
  readonly notice_period_days?: number | null;
  readonly redemption_frequency?: string | null;
}

export interface EarlyWithdrawalInput {
  readonly terms_available: boolean;
  readonly early_redemption_method?: string | null;
  readonly early_termination_retention_pct?: string | null;
}

export interface ManagementFeeInput {
  readonly terms_available: boolean;
  /** Annual basis points from funds' catalog (audit #140). A real 0 means
   *  "no management fee"; null means funds did not answer; the field being
   *  absent means the endpoint does not carry per-product fees (the
   *  portfolio listing) and callers should omit the line entirely. */
  readonly management_fee_bps?: number | null;
}

const FREQUENCY_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semi-annual',
  annual: 'Annual',
};

/** Term / lock-in label. Never a liquidity promise — liquidity is the
 *  redemption policy's story, not the term's. */
export function formatTerm(input: RedemptionTermsInput): string {
  if (!input.terms_available) return 'Terms unavailable';
  if (input.term_days) return `${input.term_days} days`;
  return 'Open-ended';
}

/** The redemption policy in one line, or null when funds did not send it
 *  (callers show a "see product details / offering document" fallback —
 *  never a guess). */
export function formatRedemption(input: RedemptionTermsInput): string | null {
  if (!input.terms_available) return 'Terms unavailable';
  const { lockup_days: lockup, notice_period_days: notice, redemption_frequency: freq } = input;
  if (lockup == null || notice == null || !freq) return null;

  const noticeSuffix = notice > 0 ? ` · ${notice}-day notice` : '';
  if (freq === 'anytime') {
    // Lock-up covering the whole term: the capital may leave once the term is complete.
    if (input.term_days != null && lockup >= input.term_days) {
      return `After the ${input.term_days}-day term — capital is committed until the term ends`;
    }
    if (lockup > 0) return `After a ${lockup}-day lock-up${noticeSuffix}`;
    return `Anytime${noticeSuffix}`;
  }
  const windowLabel = FREQUENCY_LABEL[freq] ?? freq;
  if (lockup > 0) {
    return `${windowLabel} windows after a ${lockup}-day lock-up${noticeSuffix}`;
  }
  return `${windowLabel} windows${noticeSuffix}`;
}

/** Management-fee label (audit #140): the fee must be visible wherever a
 *  yield is shown. A configured 0 reads as "None" — 0 is a real value, not
 *  an absence; only unknown terms read as unavailable. */
export function formatManagementFee(input: ManagementFeeInput): string {
  if (!input.terms_available || input.management_fee_bps == null) {
    return 'Terms unavailable';
  }
  if (input.management_fee_bps === 0) return 'None';
  return `${(input.management_fee_bps / 100).toFixed(2)}% p.a.`;
}

/** Early-withdrawal label + tone. No configured penalty means funds refuses
 *  the early exit outright — that is stated plainly, never as a free exit. */
export function formatEarlyWithdrawal(input: EarlyWithdrawalInput): {
  text: string;
  tone: 'danger' | 'neutral';
} {
  if (!input.terms_available) return { text: 'Terms unavailable', tone: 'neutral' };
  const method = input.early_redemption_method ?? null;
  const rawPct = input.early_termination_retention_pct;
  const pct = rawPct != null ? Number.parseFloat(rawPct) : Number.NaN;
  if (method === 'flat' && Number.isFinite(pct) && pct > 0) {
    return { text: `${(pct * 100).toFixed(2)}% of balance, paid to QMI`, tone: 'danger' };
  }
  if (method) {
    return { text: 'Charge paid to QMI — see the offering document', tone: 'danger' };
  }
  return { text: 'Not available — capital committed until eligibility', tone: 'neutral' };
}
